import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getTypeformUrl } from '../utils/cookies';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
export const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [showControls, setShowControls] = useState(true);
  const controlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      // Reset to start if this is the first play
      if (!hasStarted) {
        video.currentTime = 0;
      }
      video.play();
      setIsPlaying(true);
      setHasStarted(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [hasStarted]);
  const handleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);
  const handleFullscreen = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.requestFullscreen) {
      video.requestFullscreen();
    }
  }, []);
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * video.duration;
  }, []);
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    if (isPlaying) {
      controlsTimeout.current = setTimeout(() => setShowControls(false), 2500);
    }
  }, [isPlaying]);
  // Set video to 1 second on load for poster effect
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onLoadedMetadata = () => {
      // Seek to 1 second to show as poster
      video.currentTime = 1;
      setDuration(formatTime(video.duration));
    };
    const onTimeUpdate = () => {
      if (video.duration) {
        setProgress(video.currentTime / video.duration * 100);
        setCurrentTime(formatTime(video.currentTime));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, []);
  return <>
      {/* Green Section — text content */}
      <section className="relative bg-[#1a2e1c] text-white overflow-hidden pt-24 pb-48 md:pt-28 md:pb-64" data-id="element-404">
        {/* Background texture */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(216,163,77,0.06)_0%,transparent_50%)] pointer-events-none" data-id="element-405"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(216,163,77,0.04)_0%,transparent_50%)] pointer-events-none" data-id="element-406"></div>

        <div className="container mx-auto px-4 md:px-8 relative z-10" data-id="element-407">
          <div className="max-w-6xl mx-auto text-center" data-id="element-408">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8 md:mb-10" data-id="element-409">
              <span className="text-[#d8a34d] font-bold text-sm" data-id="element-410">New</span>
              <span className="text-white/90 text-sm md:text-base" data-id="element-411">
                For Indian American Investors
              </span>
            </div>

            {/* Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-white leading-[1.1] mb-6 md:mb-8 tracking-tight" data-id="element-412">
              Dubai's Property Market Is Delivering{' '}
              <span className="text-[#d8a34d]" data-id="element-413">
                15–20% Annual Capital Growth
              </span>{' '}
              + Rental Yields
            </h1>

            {/* Description */}
            <p className="text-white/70 text-lg md:text-xl lg:text-2xl font-light leading-relaxed max-w-2xl mx-auto" data-id="element-414">
              Discover how global investors are entering one of the world's
              fastest growing real estate markets
            </p>
          </div>
        </div>
      </section>

      {/* White Section — video overlaps into green, then CTA below */}
      <section className="relative pb-10 md:pb-14" data-id="element-415">
        <div className="container mx-auto px-4 md:px-8" data-id="element-416">
          <div className="max-w-6xl mx-auto text-center" data-id="element-417">
            {/* Video — pulled up into the green zone */}
            <div className="-mt-36 md:-mt-48 mb-10 md:mb-14 relative z-10" data-id="element-418">
              <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)] border border-gray-200 bg-white p-1.5 md:p-2" data-id="element-419">
                <div className="rounded-xl overflow-hidden bg-black relative group cursor-pointer" style={{
                paddingTop: '56.25%'
              }} onMouseMove={handleMouseMove} onMouseLeave={() => {
                if (isPlaying) {
                  controlsTimeout.current = setTimeout(() => setShowControls(false), 1500);
                }
              }} data-id="element-420">
                  <video ref={videoRef} src="https://ikigaire.ae/wp-content/uploads/2026/03/Video-01-Long_06-3.mp4" playsInline preload="metadata" onClick={handlePlayPause} className="absolute inset-0 w-full h-full object-cover" data-id="element-421" />

                  {/* Big play button overlay — shown before first play or when paused & not started */}
                  {!hasStarted && <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-10" onClick={handlePlayPause} data-id="element-422">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#d8a34d] flex items-center justify-center shadow-[0_0_40px_rgba(216,163,77,0.4)] hover:scale-110 transition-transform duration-200" data-id="element-423">
                        <Play className="w-7 h-7 md:w-9 md:h-9 text-white ml-1" fill="white" data-id="element-424" />
                      </div>
                    </div>}

                  {/* Custom controls bar */}
                  {hasStarted && <div className={`absolute bottom-0 left-0 right-0 z-20 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`} data-id="element-425">
                      {/* Gradient fade */}
                      <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-10 pb-3 px-4" data-id="element-426">
                        {/* Progress bar */}
                        <div ref={progressRef} className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 group/progress hover:h-2.5 transition-all" onClick={handleProgressClick} data-id="element-427">
                          <div className="h-full bg-[#d8a34d] rounded-full relative transition-all" style={{
                        width: `${progress}%`
                      }} data-id="element-428">
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/progress:opacity-100 transition-opacity" data-id="element-429" />
                          </div>
                        </div>

                        {/* Controls row */}
                        <div className="flex items-center justify-between gap-3" data-id="element-430">
                          <div className="flex items-center gap-3" data-id="element-431">
                            {/* Play/Pause */}
                            <button onClick={e => {
                          e.stopPropagation();
                          handlePlayPause();
                        }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-432">
                              {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" data-id="element-433" /> : <Play className="w-5 h-5" fill="currentColor" data-id="element-434" />}
                            </button>

                            {/* Mute */}
                            <button onClick={e => {
                          e.stopPropagation();
                          handleMute();
                        }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-435">
                              {isMuted ? <VolumeX className="w-5 h-5" data-id="element-436" /> : <Volume2 className="w-5 h-5" data-id="element-437" />}
                            </button>

                            {/* Time */}
                            <span className="text-white/70 text-xs font-medium tabular-nums" data-id="element-438">
                              {currentTime} / {duration}
                            </span>
                          </div>

                          {/* Fullscreen */}
                          <button onClick={e => {
                        e.stopPropagation();
                        handleFullscreen();
                      }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label="Fullscreen" data-id="element-439">
                            <Maximize className="w-4.5 h-4.5" data-id="element-440" />
                          </button>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            {/* Primary CTA */}
            <a href="#" onClick={e => {
            e.preventDefault();
            window.location.href = getTypeformUrl();
          }} className="btn-gold px-10 py-5 md:px-14 md:py-6 rounded-lg font-bold text-lg md:text-xl inline-block shadow-2xl animate-pulse-slow" data-id="element-441">
              Book a Free Strategy Call
            </a>
          </div>
        </div>
      </section>
    </>;
};